import { useState, useEffect, useCallback } from 'react'
import {
    FetchLiveDeviceData,
    FetchCalculatedData,
    FetchDeviceStatusNew,
    FetchCalculatedInvertersForPlant,
    FetchAllInvertersLiveData,
} from '../../../../../services/api'

// Status icons
import DS1 from '../../../../../assets/images/DS1.svg' // Yellow
import DS2 from '../../../../../assets/images/DS2.svg' // Green
import DS3 from '../../../../../assets/images/DS3.svg' // Red

// Helper functions
const getStatusIcon = (status) => {
    if (!status) return DS3
    const s = status.toLowerCase()
    if (s === 'online' || s === 'live') return DS2
    return DS1
}

const formatValue = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '—') return '—'
    const num = parseFloat(value)
    if (isNaN(num)) return '—'
    return num.toFixed(decimals)
}

export const useLiveDataView = (plantData, token) => {
    const [fetchedData, setFetchedData] = useState({
        inverters: [],
        sensor: {
            sensorID: 'SNS-001',
            statusIcon: DS3,
            status: 'OFFLINE',
            modelName: '—',
            name: '—',
            temperature: { value: '—', unit: '°C' },
            irradiance: { value: '—', unit: 'W/m²' },
            irradiation: { value: '—', unit: 'kWh/m²' },
        },
    })

    const [calculatedData, setCalculatedData] = useState({
        meter: {
            meterID: 'MTR-001',
            statusIcon: DS1,
            status: 'OFFLINE',
            lastEnergyExport: { value: '—', unit: 'kWh' },
            current: { value: '—', unit: 'kWh' },
            multiplyingFactor: { value: '1', unit: 'x' },
            todayGeneration: { value: '—', unit: 'kWh' },
            name: '—',
        },
    })

    const [calculatedInverters, setCalculatedInverters] = useState([])
    const [loading, setLoading] = useState(false)

    const fetchDeviceStatus = useCallback(async () => {
        if (!plantData?.id || !token) return null
        try {
            const response = await FetchDeviceStatusNew(plantData.id, token)
            if (response?.data?.status && response.data.data) {
                const isOnline = response.data.data.overallDeviceStatus === 'green' || response.data.data.status === 'All Online'
                const status = isOnline ? 'ONLINE' : 'OFFLINE'
                const statusMap = { 'INV-001': status, 'INV-002': status, 'MTR-001': status, 'SNS-001': status }
                return statusMap
            }
        } catch (error) {
            console.error('Error fetching device status:', error)
        }
        return null
    }, [plantData?.id, token])

    const loadFetchedData = useCallback(async (statusMap) => {
        try {
            const invertersResponse = await FetchAllInvertersLiveData(plantData.id, token)
            if (invertersResponse?.success && invertersResponse.data?.inverters) {
                const transformedInverters = invertersResponse.data.inverters.map(inv => ({
                    inverterID: inv.deviceId,
                    channel: inv.channel,
                    statusIcon: getStatusIcon(statusMap?.[inv.deviceId] || 'OFFLINE'),
                    status: statusMap?.[inv.deviceId] || 'OFFLINE',
                    name: inv.name || 'Inverter',
                    model: inv.model || '—',
                    capacity: inv.capacity || '—',
                    power: { value: formatValue(inv.power), unit: 'kW' },
                    todayGeneration: { value: formatValue(inv.todayGeneration), unit: 'kWh' },
                }))
                setFetchedData(prev => ({ ...prev, inverters: transformedInverters }))
            }

            const sensorResponse = await FetchLiveDeviceData(plantData.id, 'SNS-001', token)
            if (sensorResponse?.success && sensorResponse.data) {
                const sensorStatus = statusMap?.['SNS-001'] || 'OFFLINE'
                setFetchedData(prev => ({
                    ...prev, sensor: {
                        ...prev.sensor,
                        statusIcon: getStatusIcon(sensorStatus),
                        status: sensorStatus,
                        name: sensorResponse.data.name || 'Weather Sensor',
                        modelName: sensorResponse.data.model || '—',
                        temperature: { value: formatValue(sensorResponse.data.temperature), unit: '°C' },
                        irradiance: { value: formatValue(sensorResponse.data.irradiance), unit: 'W/m²' },
                        irradiation: { value: formatValue(sensorResponse.data.irradiation), unit: 'kWh/m²' },
                    }
                }))
            }
        } catch (error) { console.error('Error loading fetched data:', error) }
    }, [plantData?.id, token])

    const loadCalculatedData = useCallback(async (statusMap) => {
        try {
            const meterResponse = await FetchCalculatedData(plantData.id, 'MTR-001', token)
            if (meterResponse?.success && meterResponse.data) {
                const mf = meterResponse.data.multiplyingFactor || 1
                const last = parseFloat(meterResponse.data.lastEnergyMeterTillExport) || 0
                const curr = parseFloat(meterResponse.data.currentEnergyMeterTillExport) || 0
                const todayGen = (curr - last) * mf
                const mtrStatus = statusMap?.['MTR-001'] || 'OFFLINE'
                setCalculatedData(prev => ({
                    ...prev, meter: {
                        ...prev.meter,
                        name: meterResponse.data.deviceName || 'Main Energy Meter',
                        statusIcon: getStatusIcon(mtrStatus),
                        status: mtrStatus,
                        lastEnergyExport: { value: formatValue(last), unit: 'kWh' },
                        current: { value: formatValue(curr), unit: 'kWh' },
                        multiplyingFactor: { value: formatValue(mf, 1), unit: 'x' },
                        todayGeneration: { value: formatValue(todayGen), unit: 'kWh' },
                    }
                }))
            }
        } catch (error) { console.error('Error loading calculated data:', error) }
    }, [plantData?.id, token])

    const loadCalculatedInverters = useCallback(async (statusMap) => {
        try {
            const response = await FetchCalculatedInvertersForPlant(plantData.id, token)
            if (response?.success && response.data?.inverters) {
                const transformed = response.data.inverters.map(inv => {
                    const last = parseFloat(inv.lastTotalEnergy) || 0
                    const current = parseFloat(inv.currentTotalEnergy) || 0
                    const status = statusMap?.[inv.deviceId] || 'OFFLINE'
                    return {
                        inverterID: inv.deviceId,
                        channel: inv.channel,
                        statusIcon: getStatusIcon(status),
                        status: status,
                        last: { value: formatValue(last), unit: 'kWh' },
                        current: { value: formatValue(current), unit: 'kWh' },
                        todayGeneration: { value: formatValue(current - last), unit: 'kWh' },
                        model: inv.model || '—',
                        name: inv.deviceName || 'Inverter',
                    }
                })
                setCalculatedInverters(transformed)
            } else {
                setCalculatedInverters([])
            }
        } catch (error) { console.error('Error loading calculated inverters:', error) }
    }, [plantData?.id, token])

    const handleRefresh = useCallback(async () => {
        setLoading(true)
        const statusMap = await fetchDeviceStatus()
        await Promise.all([loadFetchedData(statusMap), loadCalculatedData(statusMap), loadCalculatedInverters(statusMap)])
        setLoading(false)
    }, [fetchDeviceStatus, loadFetchedData, loadCalculatedData, loadCalculatedInverters])

    useEffect(() => {
        if (plantData?.id && token) {
            handleRefresh()
            const intervalId = setInterval(handleRefresh, 300000)
            return () => clearInterval(intervalId)
        }
    }, [plantData?.id, token, handleRefresh])

    return { fetchedData, calculatedData, calculatedInverters, loading, handleRefresh }
}
